/**
 * GTM / dataLayer analytics helpers.
 *
 * All functions are safe to call from SSR or environments without dataLayer —
 * they no-op when `window` is unavailable.
 */

/** Push a raw event to dataLayer. */
export function pushEvent(name, params = {}) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: name, ...params });
}

/** SHA-256 hash a string (lowercased + trimmed) for Google Enhanced Conversions. */
async function sha256(text) {
  if (!text) return "";
  const normalized = String(text).trim().toLowerCase();
  if (!normalized || !window.crypto?.subtle) return "";
  const buf = new TextEncoder().encode(normalized);
  const digest = await window.crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/** Normalize a phone number to E.164 (US default) for Enhanced Conversions. */
function normalizePhone(raw) {
  if (!raw) return "";
  const digits = String(raw).replace(/\D+/g, "");
  if (!digits) return "";
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return `+${digits}`;
}

/**
 * Fire the `generate_lead` conversion event with a rich payload + hashed PII
 * for Google Ads / Microsoft Ads Enhanced Conversions.
 */
export async function pushGenerateLead({
  email,
  phone,
  company,
  service,
  industry,
  state,
  city,
  company_size,
  form_name = "Request a Quote",
  lead_type = "Commercial Security",
  value = 1,
} = {}) {
  const [email_sha256, phone_sha256] = await Promise.all([
    sha256(email),
    sha256(normalizePhone(phone)),
  ]);
  pushEvent("generate_lead", {
    form_name,
    lead_type,
    service: service || "",
    industry: industry || "",
    state: state || "",
    city: city || "",
    company_size: company_size || "",
    value,
    currency: "USD",
    // Google Enhanced Conversions payload (hashed)
    enhanced_conversion_data: {
      sha256_email_address: email_sha256,
      sha256_phone_number: phone_sha256,
      ...(company ? { company_name: company } : {}),
    },
  });
}

/** Quote-funnel step events. */
export function trackQuoteStarted() {
  pushEvent("quote_started", { form_name: "Request a Quote" });
}
export function trackQuoteStep(stepIndex) {
  // stepIndex is the 0-based step the user JUST COMPLETED
  const n = stepIndex + 1;
  pushEvent(`quote_step_${n}`, { form_name: "Request a Quote", step: n });
}

/** Page-view events for high-intent detail pages. */
export function trackViewService(service) {
  if (!service) return;
  pushEvent("view_service", { service: service.title || service.slug, slug: service.slug });
}
export function trackViewIndustry(industry) {
  if (!industry) return;
  pushEvent("view_industry", { industry: industry.title || industry.slug, slug: industry.slug });
}
export function trackViewSystem(system) {
  if (!system) return;
  pushEvent("view_system", { system: system.title || system.slug, slug: system.slug });
}

/** Click-tracking events (usually wired via a delegated document listener). */
export function trackPhoneClick(number, source) {
  pushEvent("phone_call_click", { phone_number: number || "", source: source || "" });
}
export function trackEmailClick(email, source) {
  pushEvent("email_click", { email: email || "", source: source || "" });
}

/** Optional: PDF download tracking (hooked up when brochures exist). */
export function trackPdfDownload({ brochure_name, industry, service } = {}) {
  pushEvent("download_pdf", {
    brochure_name: brochure_name || "",
    industry: industry || "",
    service: service || "",
  });
}

/** Optional: chat tracking (wire up when a chat widget is added). */
export function trackChatStarted(context = {}) { pushEvent("chat_started", context); }
export function trackChatLead(context = {}) { pushEvent("chat_lead", context); }
export function trackChatConversion(context = {}) { pushEvent("chat_conversion", context); }
